import {
  AjvInstance,
  type IFormGenContext,
  type IFormGenSchema,
  type TFormGenUiSchema,
  type TJSONSchema,
  getJsonSchemaRule,
  parseFormGenCustomKeywords,
} from "@artifact-management/json-dynamic-form";
import { ajvResolver } from "@hookform/resolvers/ajv";
import { useStableCallback } from "@root/utils/hooks/useStableCallback";
import { isValidPhoneNumber } from "@root/utils/phoneLib";
import type { JSONSchemaType, ValidateFunction } from "ajv";
import type { FromSchema } from "json-schema-to-ts";
import { useEffect, useRef, useState } from "react";
import {
  type DeepPartial,
  type DefaultValues,
  type FieldValues,
  type KeepStateOptions,
  type UseFormReturn,
  type UseFormSubscribe,
  useForm,
} from "react-hook-form";
import { string } from "yup";
import type { IFormGenProps } from "./index";

export type TResetForm<Values> = (
  values: DefaultValues<Values> | Values | ((formValues: Values) => Values),
  keepStateOption?: KeepStateOptions
) => void;

export interface IFormGenState {
  isDirty: boolean;
  isSubmitted: boolean;
  isValid: boolean;
  isValidating: boolean;
}

// Pas de validation sur ces formats pour le moment
const FORMGEN_AJV_FORMATS = {
  richtext: () => true,
  date: () => true,
  "date-time": () => true,
  url: () => true,
  phone: (value: string) => isValidPhoneNumber(value),
  email: (value: string) => string().email().isValidSync(value),
};

export const useParsedFormJsonSchema = <Schema extends TJSONSchema>(
  formSchema: IFormGenSchema<Schema>,
  context: IFormGenContext | undefined
): TJSONSchema => {
  const prevSchemaRef = useRef(formSchema);
  const [parsedSchema, setParsedSchema] = useState<TJSONSchema>(() =>
    parseFormGenCustomKeywords(formSchema.jsonSchema, context)
  );

  useEffect(() => {
    if (prevSchemaRef.current !== formSchema) {
      setParsedSchema(parseFormGenCustomKeywords(formSchema.jsonSchema, context));
      prevSchemaRef.current = formSchema;
    }
  }, [formSchema, context]);

  return parsedSchema;
};

export const useFormGen = <Schema extends TJSONSchema, Values = FromSchema<Schema>>({
  schema,
  defaultValues,
  context,
  onChange,
}: Omit<IFormGenProps<Schema, Values>, "onSubmit" | "renderActions">): {
  formControls: UseFormReturn<FieldValues>;
  parsedJsonSchema: TJSONSchema;
} => {
  const parsedJsonSchema = useParsedFormJsonSchema(schema, context);
  const formControls = useForm({
    resolver: ajvResolver(parsedJsonSchema as JSONSchemaType<Values>, { formats: FORMGEN_AJV_FORMATS }),
    defaultValues: defaultValues as DefaultValues<FieldValues>,
  });

  useEffect(() => {
    const subscription = formControls.watch((data) => {
      onChange?.(data as DeepPartial<Values>);
    });
    return () => subscription.unsubscribe();
  }, [formControls, onChange]);

  return { formControls, parsedJsonSchema };
};

export const useFormGenElement = <Schema extends TJSONSchema, T extends FieldValues>(
  schema: IFormGenSchema<Schema>,
  uiSchemaElement: TFormGenUiSchema<Schema>,
  subscribe: UseFormSubscribe<T>
) => {
  const [currentElementState, setCurrentElementState] = useState(uiSchemaElement);
  const prevRulesResult = useRef<boolean[]>([]);
  const ajvValidators = useRef<ValidateFunction[]>([]);
  const ajvStateValidators = useRef<(ValidateFunction | null)[]>([]);

  const triggerRulesParsing = useStableCallback((data: T, formState: IFormGenState) => {
    if (!uiSchemaElement.rules) {
      return;
    }
    const override: Partial<TFormGenUiSchema<Schema>> = {};
    const rulesResult: boolean[] = [];
    for (let i = 0; i < uiSchemaElement.rules.length; i++) {
      if (ajvStateValidators.current[i]?.(formState) === false) {
        rulesResult[i] = false;
        continue;
      }
      rulesResult[i] = ajvValidators.current[i](data);
      if (rulesResult[i]) {
        Object.assign(override, uiSchemaElement.rules[i].override);
      }
    }
    if (rulesResult.every((result, index) => result === prevRulesResult.current[index])) {
      return;
    }
    prevRulesResult.current = rulesResult;
    setCurrentElementState({ ...uiSchemaElement, ...override } as TFormGenUiSchema<Schema>);
  });

  useEffect(() => {
    if (!uiSchemaElement.rules) {
      return;
    }
    for (const rule of uiSchemaElement.rules) {
      ajvValidators.current.push(AjvInstance.compile(getJsonSchemaRule(schema, rule.rule)));
      if (rule.stateRule) {
        ajvStateValidators.current.push(AjvInstance.compile(rule.stateRule));
      } else {
        ajvStateValidators.current.push(null);
      }
    }
  }, [uiSchemaElement, schema]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only rebuilt on subscribe change. triggerRulesParsing has a stable identity and does not need to be in the dependency array.
  useEffect(() => {
    const unsubscribe = subscribe({
      formState: {
        isDirty: true,
        isSubmitted: true,
        isValid: true,
        isValidating: true,
        values: true,
      },
      callback: (formState) => {
        const formGenState: IFormGenState = {
          isDirty: formState.isDirty ?? false,
          isSubmitted: formState.isSubmitted ?? false,
          isValid: formState.isValid ?? false,
          isValidating: formState.isValidating ?? false,
        };
        triggerRulesParsing(formState.values, formGenState);
      },
    });
    return () => unsubscribe();
  }, [subscribe]);

  return currentElementState;
};

// Splits the ui schema into rows, each `newLine` element starting a new one
export const getFormGenRows = <Schema extends TJSONSchema>(uiSchema: TFormGenUiSchema<Schema>[]) =>
  uiSchema.reduce<{ startIndex: number; items: TFormGenUiSchema<Schema>[] }[]>((rows, item, index) => {
    const lastRow = rows.at(-1);
    if (!lastRow || item.newLine) {
      rows.push({ startIndex: index, items: [item] });
    } else {
      lastRow.items.push(item);
    }
    return rows;
  }, []);
